import type {
  ServicesListQuery,
  ServicesWeekDayApi,
} from "@/types/analytics-services";
import { parseServiceCategoryFilter } from "../../config/analyticsFdTabs";
import { mapServiceCategoryToApi } from "../../hooks/useServicesAnalytics";
import type { ServiceAvailabilityDay, ServiceCategoryFilter } from "./mockData";
import {
  TOP_SERVICES_PRICE_MAX,
  TOP_SERVICES_PRICE_MIN,
  type TopServicesFiltersState,
} from "./topServicesFilters";

export type ServiceCategoryFilterOption = {
  id: string;
  label: string;
};

const UI_DAY_TO_API: Record<ServiceAvailabilityDay, ServicesWeekDayApi> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

export function resolveListSegmentCategory(
  segmentParam: string | null,
  hasRapidPlan: boolean,
): ServiceCategoryFilter {
  if (hasRapidPlan) {
    return "guesteriaPartners";
  }

  return parseServiceCategoryFilter(segmentParam);
}

export function resolveListApiCategory(
  segment: ServiceCategoryFilter,
  serviceActivity: TopServicesFiltersState["serviceActivity"],
): ServicesListQuery["category"] {
  if (segment !== "all") {
    return mapServiceCategoryToApi(segment);
  }

  const { activities, services } = serviceActivity;

  if (activities && !services) {
    return "ACTIVITIES";
  }

  return "ALL";
}

function buildCategoryIdsParam(
  selectedIds: string[],
  allCategoryIds: string[],
): string[] | undefined {
  if (allCategoryIds.length === 0) {
    return undefined;
  }

  if (
    selectedIds.length === 0 ||
    selectedIds.length === allCategoryIds.length
  ) {
    return undefined;
  }

  return selectedIds;
}

function buildAvailableDaysParam(
  availability: TopServicesFiltersState["availability"],
): ServicesWeekDayApi[] | undefined {
  if (availability.length === 0) {
    return undefined;
  }

  if (availability.length === 1 && availability[0] === "all") {
    return undefined;
  }

  return availability
    .filter((day): day is ServiceAvailabilityDay => day !== "all")
    .map((day) => UI_DAY_TO_API[day]);
}

function buildPriceParams(
  priceRange: TopServicesFiltersState["priceRange"],
): Pick<ServicesListQuery, "priceFrom" | "priceTo"> {
  const [min, max] = priceRange;

  if (min === TOP_SERVICES_PRICE_MIN && max === TOP_SERVICES_PRICE_MAX) {
    return {};
  }

  return {
    priceFrom: min,
    priceTo: max,
  };
}

export function buildServicesListQuery(params: {
  hotelId: string;
  from: string;
  to: string;
  segmentParam: string | null;
  hasRapidPlan: boolean;
  filters: TopServicesFiltersState;
  nameSearch: string;
  page: number;
  pageSize: number;
  categoryOptions: ServiceCategoryFilterOption[];
}): ServicesListQuery {
  const segment = resolveListSegmentCategory(
    params.segmentParam,
    params.hasRapidPlan,
  );
  const trimmedSearch = params.nameSearch.trim();
  const categoryIds = buildCategoryIdsParam(
    params.filters.categoryIds,
    params.categoryOptions.map((option) => option.id),
  );
  const availableDays = buildAvailableDaysParam(params.filters.availability);

  return {
    hotelId: params.hotelId,
    from: params.from,
    to: params.to,
    category: resolveListApiCategory(segment, params.filters.serviceActivity),
    page: params.page,
    pageSize: params.pageSize,
    ...(trimmedSearch ? { nameSearch: trimmedSearch } : {}),
    ...buildPriceParams(params.filters.priceRange),
    ...(categoryIds ? { categoryIds } : {}),
    ...(availableDays?.length ? { availableDays } : {}),
  };
}

export function buildServicesListExportQuery(
  listQuery: ServicesListQuery,
  format: "csv" | "xlsx",
): Omit<ServicesListQuery, "page" | "pageSize"> & { format: "csv" | "xlsx" } {
  const rest = { ...listQuery };
  delete (rest as { page?: number }).page;
  delete (rest as { pageSize?: number }).pageSize;

  return {
    ...(rest as Omit<ServicesListQuery, "page" | "pageSize">),
    format,
  };
}
